import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface Company {
  id: string;
  name: string;
  slug: string | null;
  owner_id: string;
  size: string | null;
  industry: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  trial_ends_at: string | null;
  plan: string;
  status: string;
}

export interface CompanyProfile {
  id: string;
  company_id: string;
  registry_code: string | null;
  vat_number: string | null;
  country: string | null;
  address: string | null;
  billing_email: string | null;
  created_at: string;
  updated_at: string;
}

export function useCompany() {
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    if (user) {
      fetchCompany();
    } else {
      setCompany(null);
      setCompanyProfile(null);
      setLoading(false);
    }
  }, [user]);

  const fetchCompany = async () => {
    if (!user) return;
    
    setLoading(true);
    
    try {
      // Get user's company_id from profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("company_id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!profile?.company_id) {
        setLoading(false);
        return;
      }

      // Get company data
      const { data: companyData } = await supabase
        .from("companies")
        .select("*")
        .eq("id", profile.company_id)
        .maybeSingle();

      if (companyData) {
        setCompany(companyData as Company);
        setIsOwner(companyData.owner_id === user.id);

        // Get company profile
        const { data: profileData } = await supabase
          .from("company_profiles")
          .select("*")
          .eq("company_id", companyData.id)
          .maybeSingle();

        if (profileData) {
          setCompanyProfile(profileData as CompanyProfile);
        }
      }
    } catch (error) {
      console.error("Error fetching company:", error);
    }
    
    setLoading(false);
  };

  const createCompany = async (data: {
    name: string;
    size: string;
    industry: string;
    description?: string;
  }) => {
    if (!user) throw new Error("Not authenticated");

    // Create company
    const { data: newCompany, error: companyError } = await supabase
      .from("companies")
      .insert({
        name: data.name,
        owner_id: user.id,
        size: data.size,
        industry: data.industry,
        description: data.description || null,
      })
      .select()
      .single();

    if (companyError) throw companyError;

    // Update user's profile with company_id
    const { error: profileError } = await supabase
      .from("profiles")
      .update({ company_id: newCompany.id })
      .eq("user_id", user.id);

    if (profileError) {
      console.error("Error updating profile with company_id:", profileError);
      throw profileError;
    }

    // Ensure user has admin role - upsert to handle both new and existing roles
    const { data: existingRole } = await supabase
      .from("user_roles")
      .select("id, role")
      .eq("user_id", user.id)
      .maybeSingle();

    if (existingRole) {
      // Update existing role to admin if not already
      if (existingRole.role !== "admin") {
        const { error: roleUpdateError } = await supabase
          .from("user_roles")
          .update({ role: "admin" })
          .eq("id", existingRole.id);
        
        if (roleUpdateError) {
          console.error("Error updating user role:", roleUpdateError);
        }
      }
    } else {
      // Insert new admin role
      const { error: roleInsertError } = await supabase
        .from("user_roles")
        .insert({
          user_id: user.id,
          role: "admin",
        });
      
      if (roleInsertError) {
        console.error("Error inserting user role:", roleInsertError);
      }
    }

    // Create empty company profile
    const { error: companyProfileError } = await supabase
      .from("company_profiles")
      .insert({
        company_id: newCompany.id,
      });

    if (companyProfileError) {
      console.error("Error creating company profile:", companyProfileError);
    }

    // Update local state immediately
    setCompany(newCompany as Company);
    setIsOwner(true);
    setCompanyProfile({
      id: "",
      company_id: newCompany.id,
      registry_code: null,
      vat_number: null,
      country: null,
      address: null,
      billing_email: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

    return newCompany;
  };

  const updateCompany = async (data: Partial<Company>) => {
    if (!company) throw new Error("No company");

    const { error } = await supabase
      .from("companies")
      .update(data)
      .eq("id", company.id);

    if (error) throw error;

    setCompany({ ...company, ...data });
  };

  const updateCompanyProfile = async (data: Partial<CompanyProfile>) => {
    if (!company) throw new Error("No company");

    if (companyProfile) {
      const { error } = await supabase
        .from("company_profiles")
        .update(data)
        .eq("company_id", company.id);

      if (error) throw error;
      setCompanyProfile({ ...companyProfile, ...data });
    } else {
      const { data: newProfile, error } = await supabase
        .from("company_profiles")
        .insert({ ...data, company_id: company.id })
        .select()
        .single();

      if (error) throw error;
      setCompanyProfile(newProfile as CompanyProfile);
    }
  };

  const deleteCompany = async () => {
    if (!company || !isOwner) throw new Error("Not authorized");

    const { error } = await supabase
      .from("companies")
      .delete()
      .eq("id", company.id);

    if (error) throw error;

    setCompany(null);
    setCompanyProfile(null);
  };

  return {
    company,
    companyProfile,
    loading,
    isOwner,
    createCompany,
    updateCompany,
    updateCompanyProfile,
    deleteCompany,
    refetch: fetchCompany,
  };
}
